import "./style.scss";

import { Drawer, Tab, Tooltip } from "@mui/material";
import Plus from "assets/img/svg/plus";
import ModalBox from "components/Modal";
import ProgressBar from "components/shared/ProgressBar";
import SettingsHeader from "components/shared/SettingsHeader";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import { convertToRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useRef, useState } from "react";
// Imports from React
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { AgreementTemplate } from "store/agreementsStore";
import {
    deleteAgreementTemplate,
    fetchAgreementsTemplate,
    fetchAgreementsValidators,
    postAgreementTemplate,
    putAgreementTemplate,
} from "store/agreementsStore";
import { fetchChannelCompensationGroups, fetchChannels } from "store/channelStore";
// Imports from store
import type { RootState } from "store/store";
import { useAppDispatch, useAppSelector } from "store/store";
import type { IUser } from "types";

import AgreementPage from "./AgreementPage";
import AgreementSettingsTab from "./AgreementSettingsTab";
import AgreementTemplatesTab from "./AgreementTemplatesTab";
import { initialEditorState } from "./ContractEditorLexical";

const ContractsSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { agreementsTemplate, defaultAgreementTemplate } = useAppSelector((state: RootState) => state.agreements);
    const { agreementsTemplateStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);

    const initialDrawerState = {
        open: false,
        agreement: {} as AgreementTemplate,
        isDefaultTemplate: false,
    };

    const [drawer, setDrawer] = useState(initialDrawerState);
    const agreementPageRef = useRef<HTMLDivElement>(null);
    const [tabsValue, setTabsValue] = useState(0);

    const onChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
        setTabsValue(newValue);
    };

    const { data: users } = useAppSelector((state) => state.adminPanel.users.company);

    const [lastAddedTemplateId, setLastAddedTemplateId] = useState<number | null>(null);
    const [templateTypeFilter, setTemplateTypeFilter] = useState<
        "agreement" | "avenant" | "framework_contract" | undefined
    >(undefined);
    const [showTypeChoiceDialog, setShowTypeChoiceDialog] = useState(false);

    const [contractValidators, setContractValidators] = useState<{
        [key: number]: IUser[];
    }>({});

    const { getCompanySettings } = useCompanySettings();
    const usesValidationChain = getCompanySettings("uses_agreement_validation_chain");
    const authorizedAgreementTemplatesLimit = getCompanySettings("authorized_agreement_templates_limit");
    const totalTemplates = (defaultAgreementTemplate ? 1 : 0) + (agreementsTemplate?.length ?? 0);
    const hasUnlimitedTemplates =
        !authorizedAgreementTemplatesLimit || authorizedAgreementTemplatesLimit === "Infinity";
    const isTemplateLimitReached = !hasUnlimitedTemplates && totalTemplates >= +authorizedAgreementTemplatesLimit;

    useEffect(() => {
        dispatch(fetchAgreementsTemplate());
        if (!channels.length) dispatch(fetchChannels());
        if (!channelCompensationGroups.length) dispatch(fetchChannelCompensationGroups());
        dispatch(getUserListByCompany());
    }, []);

    const handleSaveTemplate = (
        data: { stringifiedBlocks: string; html: string },
        name: string,
        id: number,
        isLexical = false,
    ) => {
        const options = {
            inlineStyles: {
                center: {
                    attributes: { class: "center" },
                    style: { textAlign: "center" },
                },
            },
        };
        const newTemplate = {
            name,
            blocks: isLexical ? data.stringifiedBlocks : convertToRaw(data as any),
            html: isLexical ? data.html : stateToHTML(data as any, options),
        };
        dispatch(putAgreementTemplate({ data: newTemplate, id }));
        // setIsBlocked(false);
    };

    /**
     * Create or duplicate a new template html
     * @param _e mouse event
     * @param duplicateData duplicate HMTL, current agreement template HMTL content
     * @param duplicatedAgreementName Duplicated agreement name
     */
    const handleNewTemplate = async (
        _e: any,
        duplicateData?: any,
        duplicatedAgreementName?: string,
        type?: "agreement" | "avenant" | "framework_contract",
    ) => {
        if (isTemplateLimitReached) return;

        let name = "";
        if (duplicateData) {
            name = `${t("ContractSettings.CopyOf")} ${duplicatedAgreementName} (${agreementsTemplate?.length + 1})`;
        } else {
            name = `${t("ContractSettings.Model")} ${agreementsTemplate?.length + 1}`;
        }

        const newTemplate = {
            name,
            blocks: duplicateData?.stringifiedBlocks ?? initialEditorState,
            html: duplicateData?.html ?? "",
            type: type ?? templateTypeFilter ?? "agreement",
        };
        const response = await dispatch(postAgreementTemplate({ data: newTemplate }));
        const newAgreement = response.payload.data;
        setLastAddedTemplateId(newAgreement.id);
        setContractValidators((prevState: any) => ({
            ...prevState,
            [newAgreement.id]: [],
        }));
        handleOpenDrawer(newAgreement, false);
    };

    const handleDelete = async (id: number) => {
        setLastAddedTemplateId(null);
        await dispatch(deleteAgreementTemplate({ id }));
    };

    const isLoadingTemplates = agreementsTemplateStatus === "pending" && !agreementsTemplate.length;

    const handleOpenDrawer = (agreement: AgreementTemplate, isDefaultTemplate: boolean) => {
        setDrawer({ open: true, agreement, isDefaultTemplate });
        const id = agreement.id;
        dispatch(fetchAgreementsValidators({ id })).then((res) => {
            // Guard: thunk may resolve with a rejection payload (error body) or an
            // unexpected shape where `.data` isn't an array — never crash the drawer.
            const validators = Array.isArray((res.payload as any)?.data) ? (res.payload as any).data : [];
            setContractValidators((prevState: any) => ({
                ...prevState,
                [id]: validators.map((validator: any) => users.find((user: IUser) => +user.id === validator.userId)),
            }));
        });
    };

    enum Panel {
        TEMPLATES = 0,
        SETTINGS = 1,
    }

    return (
        <div className="settings-contracts">
            <SettingsHeader
                title={t("Settings.Contracts")}
                subtitle={t("ContractSettings.SettingsDescription")}
                renderAction={() =>
                    tabsValue === Panel.TEMPLATES ? (
                        <Tooltip
                            title={isTemplateLimitReached ? t("ContractSettings.TemplateLimitReached") : ""}
                            placement="top"
                            arrow
                            disableHoverListener={!isTemplateLimitReached}
                            disableFocusListener={!isTemplateLimitReached}
                        >
                            <span className="settings-contracts-new-template-btn">
                                <StyledButton
                                    buttonType="primary"
                                    onClick={() => setShowTypeChoiceDialog(true)}
                                    disabled={isTemplateLimitReached}
                                >
                                    <Plus />
                                    {t("ContractSettings.NewModel")}
                                </StyledButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <></>
                    )
                }
            />
            {!hasUnlimitedTemplates && (
                <div className="settings-contracts-templates-limit">
                    <ProgressBar
                        label={t("ContractSettings.UsedTemplates")}
                        value={totalTemplates}
                        total={+authorizedAgreementTemplatesLimit}
                        width={180}
                        column
                        alignStart
                    />
                </div>
            )}
            <StyledTabs style={{ marginTop: "1.5rem" }} value={tabsValue} onChange={onChangeTabs}>
                <Tab label={t("ContractSettings.Templates")} />
                <Tab label={t("ContractSettings.DisplayPreferences")} />
            </StyledTabs>

            {tabsValue === Panel.TEMPLATES && (
                <AgreementTemplatesTab
                    agreementTemplates={[defaultAgreementTemplate, ...agreementsTemplate] as AgreementTemplate[]}
                    isLoadingTemplates={isLoadingTemplates}
                    lastAddedTemplateId={lastAddedTemplateId}
                    handleOpenDrawer={handleOpenDrawer}
                    templateTypeFilter={templateTypeFilter}
                    onTemplateTypeFilterChange={setTemplateTypeFilter}
                />
            )}

            {tabsValue === Panel.SETTINGS && <AgreementSettingsTab />}

            <ModalBox
                open={showTypeChoiceDialog}
                onClose={() => setShowTypeChoiceDialog(false)}
                mainTitle={t("ContractSettings.ChooseModelType")}
            >
                <div className="flex gap-4 py-4 px-2">
                    <StyledButton
                        buttonType="outlined"
                        onClick={() => {
                            setShowTypeChoiceDialog(false);
                            handleNewTemplate(null, undefined, undefined, "agreement");
                        }}
                        fullWidth
                    >
                        {t("ContractSettings.TypeAgreement")}
                    </StyledButton>
                    <StyledButton
                        buttonType="outlined"
                        onClick={() => {
                            setShowTypeChoiceDialog(false);
                            handleNewTemplate(null, undefined, undefined, "avenant");
                        }}
                        fullWidth
                    >
                        {t("ContractSettings.TypeAvenant")}
                    </StyledButton>
                    <StyledButton
                        buttonType="outlined"
                        onClick={() => {
                            setShowTypeChoiceDialog(false);
                            handleNewTemplate(null, undefined, undefined, "framework_contract");
                        }}
                        fullWidth
                    >
                        {t("ContractSettings.TypeFrameworkContract")}
                    </StyledButton>
                </div>
            </ModalBox>

            {/** Agreement Template Editor Drawer */}
            <Drawer
                open={drawer.open}
                anchor="bottom"
                sx={{ ".MuiPaper-root": { border: "none", backgroundColor: "var(--backgroundZ2)" } }}
                PaperProps={{ ref: agreementPageRef }}
            >
                <AgreementPage
                    agreement={drawer.agreement}
                    isDefaultTemplate={drawer.isDefaultTemplate}
                    handleDelete={handleDelete}
                    handleNewTemplate={handleNewTemplate}
                    handleSaveTemplate={handleSaveTemplate}
                    handleClose={() => setDrawer(initialDrawerState)}
                    usesValidationChain={usesValidationChain}
                    isTemplateLimitReached={isTemplateLimitReached}
                    validators={contractValidators[drawer.agreement.id]}
                    agreementPageRef={agreementPageRef}
                    isDrawerOpen={drawer.open}
                />
            </Drawer>
        </div>
    );
};

export default ContractsSettings;
